import { useEffect, useState, useCallback } from 'react'
import { Scale, Plus, X, Trash2, RefreshCw, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Finding {
  id: number
  policy_name: string
  source_tool: string
  repo: string
  file_path: string
  severity: string
  message: string
  status: string
  finding_at: string
}

const SEVS = ['critical', 'high', 'medium', 'low'] as const
const TOOLS = ['opa', 'conftest', 'checkov', 'trivy', 'kubescape', 'kyverno']
const SEV_TONE: Record<string, string> = {
  critical: 'bg-rose/15 text-rose border-rose/30',
  high:     'bg-amber/15 text-amber-light border-amber/30',
  medium:   'bg-blue/15 text-blue-light border-blue/30',
  low:      'bg-emerald/15 text-emerald border-emerald/30',
}

export default function PolicyPage() {
  const [items, setItems] = useState<Finding[]>([])
  const [byRepo, setByRepo] = useState<{ repo: string; n: number; sev_high: number }[]>([])
  const [byTool, setByTool] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('open')
  const [form, setForm] = useState({
    policy_name: '', source_tool: 'opa', repo: '', file_path: '',
    severity: 'medium', message: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sre/policy', { params: { status: statusFilter } })
      setItems(r.data?.findings || [])
      setByRepo(r.data?.by_repo || [])
      setByTool(r.data?.by_tool || {})
    } finally { setLoading(false) }
  }, [statusFilter])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.policy_name) return
    await api.post('/api/sre/policy', form)
    setShowForm(false)
    setForm({ ...form, policy_name: '', message: '' })
    fetchAll()
  }

  const resolve = async (id: number) => {
    await api.post(`/api/sre/policy/${id}/resolve`, {})
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete finding?')) return
    await api.delete(`/api/sre/policy/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Scale size={24} className="text-blue-light" /> Policy-as-Code Findings
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            OPA / Conftest / Checkov / Trivy / Kubescape rollup. Push from CI via <code className="font-mono">/api/sre/policy</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Log finding</button>
        </div>
      </div>

      {/* Per-tool tile row */}
      {Object.keys(byTool).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.entries(byTool).map(([tool, n]) => (
            <div key={tool} className="card p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">{tool}</div>
              <div className="text-2xl font-bold mt-1 font-mono text-blue-light">{n}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-repo rollup */}
      {byRepo.length > 0 && (
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-text-primary mb-2">By repo (open findings)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {byRepo.slice(0, 18).map(r => (
              <div key={r.repo} className="rounded p-2 border border-border bg-surface-card">
                <div className="font-mono text-[10px] font-bold text-text-primary truncate" title={r.repo}>{r.repo}</div>
                <div className="text-[10px] text-text-muted">
                  {r.n} open
                  {r.sev_high > 0 && <span className="text-rose ml-1">· {r.sev_high} high+</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {(['open', 'resolved'] as const).map(s => (
          <button key={s} type="button" onClick={() => setStatusFilter(s)}
            className={clsx('badge text-[10px] px-2 py-1',
              statusFilter === s ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {s}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Log policy finding</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.policy_name} onChange={e => setForm({...form, policy_name: e.target.value})} placeholder='Policy e.g. "kubernetes.no_root_containers"' className="input py-1.5 md:col-span-2 font-mono" />
            <select value={form.source_tool} onChange={e => setForm({...form, source_tool: e.target.value})} className="input py-1.5">
              {TOOLS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} className="input py-1.5">
              {SEVS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={form.repo} onChange={e => setForm({...form, repo: e.target.value})} placeholder="Repo" className="input py-1.5 font-mono" />
            <input value={form.file_path} onChange={e => setForm({...form, file_path: e.target.value})} placeholder="File path" className="input py-1.5 font-mono" />
            <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={2} placeholder="Message" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        {items.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No policy findings.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Policy</th>
                <th className="text-left py-2 px-2">Repo</th>
                <th className="text-left py-2 px-2">Severity</th>
                <th className="text-left py-2 px-2">Tool</th>
                <th className="text-left py-2 px-2">Found</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2">
                    <div className="font-mono text-[10px] text-blue-light">{f.policy_name}</div>
                    {f.message && <div className="text-[10px] text-text-muted line-clamp-1">{f.message}</div>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-secondary">
                    {f.repo}
                    {f.file_path && <div className="text-[9px] text-text-muted truncate max-w-[200px]">{f.file_path}</div>}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={clsx('badge text-[9px] uppercase border', SEV_TONE[f.severity])}>{f.severity}</span>
                  </td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{f.source_tool}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{f.finding_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 px-2 text-right space-x-2">
                    {f.status === 'open' && (
                      <button type="button" onClick={() => resolve(f.id)} className="text-emerald" title="Resolve"><CheckCircle2 size={11} /></button>
                    )}
                    <button type="button" onClick={() => remove(f.id)} className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
